import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { 
  Plus, 
  Edit2, 
  Trash2,
  MapPin,
  Navigation
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Locations = () => {
  const { isAdmin } = useAuth();
  const { t, language } = useLanguage();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Form
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    latitude: '',
    longitude: '',
    radius: 100
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API_URL}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        radius: parseInt(formData.radius)
      };
      
      if (editingLocation) {
        await axios.put(`${API_URL}/locations/${editingLocation.id}`, data);
      } else {
        await axios.post(`${API_URL}/locations`, data);
      }
      fetchLocations();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      alert(error.response?.data?.detail || t('error_occurred'));
    }
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      address: location.address,
      latitude: location.latitude.toString(),
      longitude: location.longitude.toString(),
      radius: location.radius
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('confirm_delete'))) {
      try {
        await axios.delete(`${API_URL}/locations/${id}`);
        fetchLocations();
      } catch (error) {
        alert(error.response?.data?.detail || t('error_occurred'));
      }
    }
  };

  const resetForm = () => {
    setEditingLocation(null);
    setFormData({ name: '', address: '', latitude: '', longitude: '', radius: 100 });
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData(prev => ({
            ...prev,
            latitude: position.coords.latitude.toFixed(6),
            longitude: position.coords.longitude.toFixed(6)
          }));
        },
        (error) => {
          alert(language === 'es' ? 'No se pudo obtener la ubicación' : 'Could not get location');
        }
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="locations-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">{t('locations')}</h1>
          <p className="text-slate-500">
            {language === 'es' ? 'Gestión de ubicaciones permitidas para fichajes' : 'Manage allowed locations for clocking'}
          </p>
        </div>
        {isAdmin() && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-location-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('add_location')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">
                  {editingLocation ? t('edit') : t('add_location')}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('name')}</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={language === 'es' ? 'Ej: Oficina Central' : 'Ex: Main Office'}
                    required
                    data-testid="location-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('address')}</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    placeholder={language === 'es' ? 'Dirección completa' : 'Full address'}
                    required
                    data-testid="location-address-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('latitude')}</Label>
                    <Input
                      type="number"
                      step="any"
                      value={formData.latitude}
                      onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
                      placeholder="40.416775"
                      required
                      data-testid="location-latitude-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('longitude')}</Label>
                    <Input
                      type="number"
                      step="any"
                      value={formData.longitude}
                      onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
                      placeholder="-3.703790"
                      required
                      data-testid="location-longitude-input"
                    />
                  </div>
                </div>
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full"
                  onClick={getCurrentLocation}
                >
                  <Navigation className="w-4 h-4 mr-2" />
                  {language === 'es' ? 'Usar mi ubicación actual' : 'Use my current location'}
                </Button>
                <div className="space-y-2">
                  <Label>{t('radius')}</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="10"
                      max="5000"
                      value={formData.radius}
                      onChange={(e) => setFormData(prev => ({ ...prev, radius: parseInt(e.target.value) }))}
                      required
                      data-testid="location-radius-input"
                    />
                    <span className="text-sm text-slate-500">metros</span>
                  </div>
                  <p className="text-xs text-slate-400">
                    {language === 'es' 
                      ? 'Radio máximo permitido para fichajes desde esta ubicación'
                      : 'Maximum allowed radius for clocking from this location'}
                  </p>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
                    {t('cancel')}
                  </Button>
                  <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700" data-testid="location-submit-btn">
                    {t('save')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Locations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {locations.length === 0 ? (
          <Card className="border-slate-200 col-span-full">
            <CardContent className="p-8 text-center text-slate-500">
              {t('no_results')}
            </CardContent>
          </Card>
        ) : (
          locations.map((location) => (
            <Card key={location.id} className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`location-card-${location.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{location.name}</h3>
                      <Badge variant={location.is_active ? 'default' : 'secondary'} className={location.is_active ? 'bg-emerald-100 text-emerald-800' : ''}>
                        {location.is_active ? t('active') : t('inactive')}
                      </Badge>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-2">{location.address}</p>
                <div className="flex items-center gap-4 text-xs text-slate-400">
                  <span className="font-mono">
                    {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-indigo-400" />
                    {location.radius}m
                  </span>
                </div>
                {isAdmin() && (
                  <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => handleEdit(location)}
                    >
                      <Edit2 className="w-3 h-3 mr-1" />
                      {t('edit')}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(location.id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default Locations;
